import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiBaseUrl } from '../../constants/common.constants';
import { IAutomaticTextTranslation } from '../../interfaces/ai/automatic-text-translation';
import { ITranslateTextRequest } from '../../interfaces/ai/translate-text-request';
import { ITranslationResponse } from '../../interfaces/ai/translation-response';
import { IActivityRequest } from '../../interfaces/common/activity-request.interface';
import { CommonRequestParams } from '../../interfaces/common/common-request-params';
import { DataSourceResponse } from '../../interfaces/data/datasourceresponse.interface';
import { GetStructResponse } from '../../interfaces/data/get-struct-response.interface';
import { IApplicationPhone, IApplicationPhoneModule } from '../../interfaces/voice/application-phone';
import { IApplicationPhoneVersion } from '../../interfaces/voice/application-phone-version';
import { IJsonDesignerBehaviorRules } from '../../interfaces/voice/json-designer-behavior-rule';
import { ITextToSpeechCache } from '../../interfaces/voice/text-to-speech-cache';
import { IMusicOnHoldClass } from '../../interfaces/voice/music-on-hold-class';

@Injectable()
export class ApplicationPhoneService {

  private _baseUrl: string = `${ApiBaseUrl}applicationphone`;

  constructor(private httpClient: HttpClient) { }

  getApplicationsPhone(request: CommonRequestParams, type: string): Observable<DataSourceResponse<IApplicationPhone>> {
    return this.httpClient.post<DataSourceResponse<IApplicationPhone>>(`${this._baseUrl}/all/${type}`, request);
  }

  getAllApplicationsPhone(filter: string, type: string): Observable<IApplicationPhone[]> {
    return this.httpClient.get<IApplicationPhone[]>(`${this._baseUrl}/all/${type}/${filter}`);
  }

  getApplicationPhoneById(applicationId): Observable<IApplicationPhone> {
    return this.httpClient.get<IApplicationPhone>(`${this._baseUrl}/${applicationId}`);
  }

  createApplicationPhone(applicationDto: IApplicationPhone): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/create`, applicationDto);
  }

  validateJsonText(jsonText: string): Observable<boolean> {
    const params = new HttpParams().set("jsonText", jsonText)
    return this.httpClient.post<boolean>(`${this._baseUrl}/validate`, params);
  }

  verifiUniqueIdentifier(uniqueIdentifier: string, applicationId: string, customerCode: string = ''): Observable<boolean> {
    const params = new HttpParams()
      .set("uniqueIdentifier", uniqueIdentifier)
      .set("applicationId", applicationId)
      .set("code", customerCode);

    return this.httpClient.post<boolean>(`${this._baseUrl}/identifier`, params);
  }

  getJsonDesignerBehaviorRules(): Observable<IJsonDesignerBehaviorRules[]> {
    return this.httpClient.get<IJsonDesignerBehaviorRules[]>(`${this._baseUrl}/rules`);
  }

  deleteApplicationPhone(applicationId): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._baseUrl}/delete/${applicationId}`);
  }

  getApplicationsPhoneForAdmin(customerCode: string): Observable<IApplicationPhone[]> {
    return this.httpClient.get<IApplicationPhone[]>(`${this._baseUrl}/admin-all/${customerCode}`);
  }

  getVoiceIds(languageCode: string, code: string) {
    const params = new HttpParams()
      .set("code", code);

    return this.httpClient.get<string[]>(`${this._baseUrl}/voice/${languageCode}`, { params });
  }

  getStoragePeriods() {
    return this.httpClient.get<any[]>(`${this._baseUrl}/storage-periods`);
  }

  getAllMusicOnHoldClasses(customerCode) {
    return this.httpClient.get<IMusicOnHoldClass[]>(`${this._baseUrl}/all-music-on-hold-classes/${customerCode}`);
  }

  getMusicOnHoldClasses(page: number, pageSize: number): Observable<DataSourceResponse<IMusicOnHoldClass>> {
    return this.httpClient.get<DataSourceResponse<IMusicOnHoldClass>>(`${this._baseUrl}/music-on-hold-classes/${page}/${pageSize}`);
  }

  createMusicOnHoldClass(mohClass: IMusicOnHoldClass): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this._baseUrl}/create-music-on-hold-class`, mohClass);
  }

  updateMusicOnHoldClass(mohClass: IMusicOnHoldClass): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this._baseUrl}/update-music-on-hold-class`, mohClass);
  }

  deleteMusicOnHoldClass(mohClass: IMusicOnHoldClass): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this._baseUrl}/delete-music-on-hold-class`, mohClass);
  }

  translateApplicationPhone(applicationId: any, version: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._baseUrl}/translate/${applicationId}/${version}`);
  }

  translate(request: ITranslateTextRequest): Observable<GetStructResponse<ITranslationResponse>> {
    return this.httpClient.post<GetStructResponse<ITranslationResponse>>(`${this._baseUrl}/translate`, request);
  }

  getTranslationBilling(request: IActivityRequest, ownTransactions: boolean): Observable<DataSourceResponse<IAutomaticTextTranslation>> {
    return this.httpClient.post<DataSourceResponse<IAutomaticTextTranslation>>(`${this._baseUrl}/translation-billing/${ownTransactions}`, request);
  }

  getTimeSettings(): Observable<number> {
    return this.httpClient.get<number>(`${this._baseUrl}/time`);
  }

  getTexToSpeechCache(phoneId: any, isModule = false, version?: number): Observable<ITextToSpeechCache[]> {
    return this.httpClient.get<ITextToSpeechCache[]>(`${this._baseUrl}/cache-texts/${phoneId}/${isModule}/${version}`);
  }

  clearAllNodesCache(phoneId: any, applicationCustomerCode: string, version: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._baseUrl}/delete-all-cache-texts/${phoneId}/${applicationCustomerCode}/${version}`);
  }

  clearNodeCache(phoneId: any, nodeCacheId: any, isModule = false, version: number = 0): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._baseUrl}/delete-cache-texts/${phoneId}/${nodeCacheId}/${isModule}/${version}`);
  }

  //modules

  getApplicationPhoneModules(request: CommonRequestParams): Observable<DataSourceResponse<IApplicationPhoneModule>> {
    return this.httpClient.post<DataSourceResponse<IApplicationPhoneModule>>(`${this._baseUrl}/all-modules`, request);
  }

  getApplicationPhoneModuleById(applicationId): Observable<IApplicationPhoneModule> {
    return this.httpClient.get<IApplicationPhoneModule>(`${this._baseUrl}/module/${applicationId}`);
  }

  createApplicationPhoneModule(applicationDto: IApplicationPhoneModule): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/create-module`, applicationDto);
  }

  verifyModuleUniqueIdentifier(uniqueIdentifier: string, applicationId: string, customerCode: string = ''): Observable<boolean> {
    const params = new HttpParams()
      .set("uniqueIdentifier", uniqueIdentifier)
      .set("applicationId", applicationId)
      .set("code", customerCode);

    return this.httpClient.post<boolean>(`${this._baseUrl}/module-identifier`, params);
  }

  deleteApplicationPhoneModule(applicationId): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._baseUrl}/delete-module/${applicationId}`);
  }

  translateApplicationPhoneModule(applicationId: any): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._baseUrl}/translate-module/${applicationId}`);
  }

  getAllApplicationPhoneModules(customerCode: string): Observable<IApplicationPhoneModule[]> {
    if (customerCode == null) {
      customerCode = "";
    }
    return this.httpClient.get<IApplicationPhoneModule[]>(`${this._baseUrl}/all-modules/${customerCode}`);
  }

  // phone version
  getPhoneAppVersions(applicationId: any): Observable<IApplicationPhoneVersion[]> {
    return this.httpClient.get<IApplicationPhoneVersion[]>(`${this._baseUrl}/phone-versions/${applicationId}`);
  }

  createPhoneAppVersion(version: IApplicationPhoneVersion): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/create-version`, version);
  }

  updatePhoneAppVersion(version: IApplicationPhoneVersion): Observable<GetStructResponse<boolean>> {
    return this.httpClient.post<GetStructResponse<boolean>>(`${this._baseUrl}/update-version`, version);
  }

  deletePhoneAppVersion(applicationId: any, version: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this._baseUrl}/delete-version/${applicationId}/${version}`);
  }
}
